/** Adapters from the generated token maps (flat values and `{ _light, _dark }` pairs) to Chakra's format: a
 * `{ value }` per token, `{category.path}` refs. Having `build/generate.ts` emit this would retire them. */

type AnyObj = Record<string, any>;

const isPlainObject = (value: unknown): value is AnyObj =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/** Wraps primitive foundation tokens: every leaf becomes `{ value: leaf }`, preserving nesting (e.g. color
 * scales, `sizes.container`). */
export function wrapTokens(obj: AnyObj): AnyObj {
  const out: AnyObj = {};
  for (const [key, value] of Object.entries(obj)) {
    out[key] = isPlainObject(value) ? wrapTokens(value) : { value };
  }
  return out;
}

const colorRef = (value: string): string => `{colors.${value}}`;

/** Wraps semantic colors: `{ _light, _dark }` pairs become `{ value: { base, _dark } }`, nested scales recurse,
 * and every leaf string becomes a `{colors.<leaf>}` reference to a foundation color. */
export function wrapSemanticColors(obj: AnyObj): AnyObj {
  const out: AnyObj = {};
  for (const [key, value] of Object.entries(obj)) {
    if (isPlainObject(value)) {
      if ('_light' in value || '_dark' in value) {
        const base = value._light ?? value._dark;
        out[key] = { value: { base: colorRef(base), _dark: colorRef(value._dark ?? base) } };
      } else {
        out[key] = wrapSemanticColors(value);
      }
    } else {
      out[key] = { value: colorRef(value) };
    }
  }
  return out;
}

/** Wraps single-category semantic tokens (spacing, fontWeights, shadows) whose leaves reference a same-category
 * foundation token; values in `literals` (e.g. `'none'`) are emitted verbatim. */
export function wrapSemanticRefs(obj: AnyObj, category: string, literals: string[] = []): AnyObj {
  const out: AnyObj = {};
  for (const [key, value] of Object.entries(obj)) {
    if (isPlainObject(value)) {
      out[key] = wrapSemanticRefs(value, category, literals);
    } else {
      out[key] = { value: literals.includes(String(value)) ? value : `{${category}.${value}}` };
    }
  }
  return out;
}

/** Wraps composite text styles: each named style becomes `{ value: styleObject }`; Chakra resolves the inner
 * fontSize/lineHeight/fontFamily/fontWeight against their token scales. */
export function wrapTextStyles(obj: AnyObj): AnyObj {
  const out: AnyObj = {};
  for (const [key, value] of Object.entries(obj)) {
    out[key] = { value };
  }
  return out;
}
